import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Title2,
  Body1,
  Text,
  Input,
  Select,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  type DataGridProps,
  type TableColumnSizingOptions,
  SearchBox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Spinner,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUpRegular,
  ArrowDownRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  ArrowClockwiseDashesRegular,
  DismissRegular,
  DiamondFilled,
  ArrowDownloadRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
} from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useApiRequest } from '../hooks/useApiRequest';
import { ApiService } from '../services/apiService';
import { getFriendlyApiErrorMessage, logApiError } from '../services/apiErrorMessages';
import { exportToCsv } from '../utils/exportToCsv';
import { trackEvent } from '../services/telemetry';
import { useChangeHistoryPageStyles } from '../styles/ChangeHistoryPage.styles';
import { getTierColor, TIER_OPTIONS } from '../constants/tiers';
import { formatCurrency, normalizeStatus, formatPacificDateTimeParts, getPacificTimeZoneAbbr } from '../utils/formatters';
import { compareString, compareNumber, compareTier } from '../utils/comparators';
import { PAGE_SIZE, SEARCH_DEBOUNCE_MS } from '../constants/table';
import type { RetiredTier, Tier } from '../constants/tiers';
import type { ApprovalStatus } from '../utils/formatters';
import { SUMMARY_CARD_COLORS, SummaryCardBar } from '../components/SummaryCardBar';
import { usePartnerAliases } from '../contexts/PartnerAliasContext';

type ChangeType = 'Promotion' | 'Demotion';

interface TierChangeRecord {
  id: string;
  mpnId: string;
  type: ChangeType;
  previousTier: Tier | RetiredTier;
  newTier: Tier | RetiredTier;
  status: string;
  changedBy: string;
  timestamp: string;
  partnerName: string;
  contractLocation: string;
  contractStatus: string;
  globalMpnId: string;
  tpid: string;
  ttmRevenue: number | null;
  nextTtm: number | null;
  futureTtm: number | null;
}

interface ChangeHistorySummary {
  total: number;
  promotions: number;
  demotions: number;
  page: number;
  pageSize: number;
}

interface ChangeHistoryResponse {
  summary: ChangeHistorySummary;
  records: TierChangeRecord[];
}

const TIME_RANGES = [
  'All time',
  'Last 1 month',
  'Last 3 months',
  'Last 6 months',
  'Last 1 year',
  'Last 2 years',
  'Custom',
] as const;

type TimeRangeLabel = (typeof TIME_RANGES)[number];

const toApiTimeRange = (label: TimeRangeLabel): string => label.replace(/\s+/g, '');

const COLUMN_IDS = [
  'partnerName', 'type', 'contractLocation', 'contractStatus',
  'globalMpnId', 'locationMpnId', 'tpid', 'previousTier',
  'ttmRevenue', 'nextTtmForOps', 'futureTtm', 'newTier',
  'status', 'changedBy', 'dateTime',
] as const;

type ChangeHistoryColumnId = (typeof COLUMN_IDS)[number];
const PACIFIC_ZONE = getPacificTimeZoneAbbr();

const COLUMN_SIZING: Record<ChangeHistoryColumnId, { minWidth: number; weight: number }> = {
  partnerName:      { minWidth: 180, weight: 4.5 },
  type:             { minWidth: 100, weight: 0.4 },
  contractLocation: { minWidth: 130, weight: 0.4 },
  contractStatus:   { minWidth: 109, weight: 0.4 },
  globalMpnId:      { minWidth: 110, weight: 0.4 },
  locationMpnId:    { minWidth: 124, weight: 0.4 },
  tpid:             { minWidth: 80,  weight: 0.4 },
  previousTier:     { minWidth: 95,  weight: 0.4 },
  ttmRevenue:       { minWidth: 100, weight: 0.4 },
  nextTtmForOps:    { minWidth: 135, weight: 0.4 },
  futureTtm:        { minWidth: 94,  weight: 0.4 },
  newTier:          { minWidth: 95,  weight: 0.4 },
  status:           { minWidth: 95,  weight: 0.4 },
  changedBy:        { minWidth: 129, weight: 0.4 },
  dateTime:         { minWidth: 100, weight: 0.4 },
};

const columns: TableColumnDefinition<TierChangeRecord>[] = [
  createTableColumn<TierChangeRecord>({
    columnId: 'partnerName',
    compare: (a, b) => compareString(a.partnerName, b.partnerName),
    renderHeaderCell: () => 'Partner name',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'type',
    compare: (a, b) => compareString(a.type, b.type),
    renderHeaderCell: () => 'Type',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'contractLocation',
    compare: (a, b) => compareString(a.contractLocation, b.contractLocation),
    renderHeaderCell: () => 'Contract location',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'contractStatus',
    compare: (a, b) => compareString(a.contractStatus, b.contractStatus),
    renderHeaderCell: () => 'Contract status',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'globalMpnId',
    compare: (a, b) => compareString(a.globalMpnId, b.globalMpnId),
    renderHeaderCell: () => 'Global MPN ID',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'locationMpnId',
    compare: (a, b) => compareString(a.mpnId, b.mpnId),
    renderHeaderCell: () => 'Location MPN ID',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'tpid',
    compare: (a, b) => compareString(a.tpid, b.tpid),
    renderHeaderCell: () => 'TPID',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'previousTier',
    compare: (a, b) => compareTier(a.previousTier, b.previousTier),
    renderHeaderCell: () => 'Previous tier',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'ttmRevenue',
    compare: (a, b) => compareNumber(a.ttmRevenue, b.ttmRevenue),
    renderHeaderCell: () => 'TTM revenue',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'nextTtmForOps',
    compare: (a, b) => compareNumber(a.nextTtm, b.nextTtm),
    renderHeaderCell: () => 'Next TTM (for Ops)',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'futureTtm',
    compare: (a, b) => compareNumber(a.futureTtm, b.futureTtm),
    renderHeaderCell: () => 'Future TTM',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'newTier',
    compare: (a, b) => compareTier(a.newTier, b.newTier),
    renderHeaderCell: () => 'New tier',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'status',
    compare: (a, b) => compareString(a.status, b.status),
    renderHeaderCell: () => 'Status',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'changedBy',
    compare: (a, b) => compareString(a.changedBy, b.changedBy),
    renderHeaderCell: () => 'Changed by',
    renderCell: () => null,
  }),
  createTableColumn<TierChangeRecord>({
    columnId: 'dateTime',
    compare: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    renderHeaderCell: () => `Date (${PACIFIC_ZONE})`,
    renderCell: () => null,
  }),
];

export const ChangeHistoryPage: React.FC = () => {
  const styles = useChangeHistoryPageStyles();
  const { getDisplayName } = usePartnerAliases();

  const [filterType, setFilterType] = useState<'All' | ChangeType>('All');
  const [filterStatus, setFilterStatus] = useState<'All' | 'Approved' | 'Denied'>('All');
  const [filterPreviousTier, setFilterPreviousTier] = useState('All');
  const [filterNewTier, setFilterNewTier] = useState('All');
  const [filterTimeRange, setFilterTimeRange] = useState<TimeRangeLabel>('All time');
  const [filterChangedBy, setFilterChangedBy] = useState('');
  const [filterChangedByQuery, setFilterChangedByQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateFromInputRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const hasActiveFilters =
    filterType !== 'All' ||
    filterStatus !== 'All' ||
    filterPreviousTier !== 'All' ||
    filterNewTier !== 'All' ||
    filterTimeRange !== 'All time' ||
    filterChangedBy.trim() !== '' ||
    searchInput.trim() !== '' ||
    dateFrom !== '' ||
    dateTo !== '';

  const clearAllFilters = useCallback(() => {
    setFilterType('All');
    setFilterStatus('All');
    setFilterPreviousTier('All');
    setFilterNewTier('All');
    setFilterTimeRange('All time');
    setFilterChangedBy('');
    setFilterChangedByQuery('');
    setSearchInput('');
    setDateFrom('');
    setDateTo('');
  }, []);

  // Track the table-wrapper width so we can re-distribute leftover horizontal space across the columns whenever the viewport (or sidebar / drawer) changes.
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const totalMin = COLUMN_IDS.reduce((sum, id) => sum + COLUMN_SIZING[id].minWidth, 0);
    const totalWeight = COLUMN_IDS.reduce((sum, id) => sum + COLUMN_SIZING[id].weight, 0);
    const SAFETY_BUFFER = 8;
    const available = Math.max(containerWidth - SAFETY_BUFFER, totalMin);
    const extra = Math.max(0, available - totalMin);

    const sizing: TableColumnSizingOptions = {};
    COLUMN_IDS.forEach((id) => {
      const { minWidth, weight } = COLUMN_SIZING[id];
      const ideal = totalWeight > 0
        ? minWidth + Math.floor((extra * weight) / totalWeight)
        : minWidth;
      sizing[id] = { minWidth, idealWidth: ideal };
    });
    return sizing;
  }, [containerWidth]);

  const { data, isLoading, error, execute } = useApiRequest<ChangeHistoryResponse>();

  // Debounce free-text inputs (search + changed by) so each keystroke doesn't
  // hit the API.
  useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const handle = setTimeout(() => setFilterChangedByQuery(filterChangedBy.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [filterChangedBy]);

  useEffect(
    () => setCurrentPage(1),
    [
      filterType,
      filterStatus,
      filterPreviousTier,
      filterNewTier,
      filterTimeRange,
      filterChangedByQuery,
      searchQuery,
      dateFrom,
      dateTo,
    ],
  );

  const requestBody = useMemo(() => {
    const body: Record<string, unknown> = {
      timeRange: toApiTimeRange(filterTimeRange),
      page: currentPage,
      pageSize: PAGE_SIZE,
    };
    if (filterType !== 'All') body.type = filterType;
    if (filterStatus !== 'All') body.status = filterStatus;
    if (filterPreviousTier !== 'All') body.previousTier = filterPreviousTier;
    if (filterNewTier !== 'All') body.newTier = filterNewTier;
    if (filterChangedByQuery) body.changedBy = filterChangedByQuery;
    if (searchQuery) body.search = searchQuery;
    if (filterTimeRange === 'Custom') {
      if (dateFrom) body.dateFrom = dateFrom;
      if (dateTo) body.dateTo = dateTo;
    }
    return body;
  }, [
    filterType,
    filterStatus,
    filterPreviousTier,
    filterNewTier,
    filterTimeRange,
    filterChangedByQuery,
    searchQuery,
    dateFrom,
    dateTo,
    currentPage,
  ]);

  const customIncomplete =
    filterTimeRange === 'Custom' && (!dateFrom || !dateTo);

  useEffect(() => {
    if (customIncomplete) return;
    execute((api) => api.post<ChangeHistoryResponse>('/TierChange/changeHistory', requestBody));
  }, [execute, requestBody, customIncomplete]);

  const summary = data?.summary;
  const records = data?.records ?? [];

  // Keep a stable "all-types" snapshot for the summary cards so their counts
  // don't change when the user clicks a card to filter by type. The snapshot
  // refreshes only when the API response itself was fetched with filterType 'All'.
  // Using a ref for filterType means this effect only runs when `data` changes —
  // not when filterType changes — avoiding a flash of stale filtered counts.
  const filterTypeRef = useRef(filterType);
  filterTypeRef.current = filterType;
  const [cardSummary, setCardSummary] = useState<{ total: number; promotions: number; demotions: number } | null>(null);
  useEffect(() => {
    if (filterTypeRef.current === 'All' && summary) {
      setCardSummary({ total: summary.total, promotions: summary.promotions, demotions: summary.demotions });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  const totalChanges = summary?.total ?? 0;
  const promotions = cardSummary?.promotions ?? summary?.promotions ?? 0;
  const demotions = cardSummary?.demotions ?? summary?.demotions ?? 0;
  const cardTotal = cardSummary?.total ?? summary?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalChanges / PAGE_SIZE));
  const startIndex = totalChanges === 0 ? 0 : (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + records.length, totalChanges);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const pageOptions = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages],
  );

  const { instance, accounts } = useMsal();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const isFilterDebouncePending =
    searchInput.trim() !== searchQuery || filterChangedBy.trim() !== filterChangedByQuery;

  const canExport = !isExporting && !customIncomplete && !isFilterDebouncePending && totalChanges > 0;

  const handleExport = useCallback(async () => {
    if (customIncomplete) {
      setExportError('Select both Date From and Date To to export results.');
      return;
    }
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) return;
    const api = new ApiService(instance, account);

    setIsExporting(true);
    setExportError(null);
    try {
      const EXPORT_PAGE_SIZE = 200;
      const all: TierChangeRecord[] = [];
      let page = 1;
      let totalCount = Infinity;
      while (all.length < totalCount) {
        const response = await api.post<ChangeHistoryResponse>('/TierChange/changeHistory', {
          ...requestBody,
          page,
          pageSize: EXPORT_PAGE_SIZE,
        });
        all.push(...response.records);
        totalCount = response.summary.total;
        if (response.records.length === 0) break;
        page += 1;
      }

      trackEvent('change_history_export', { rows: all.length });
      exportToCsv<TierChangeRecord>({
        fileName: 'change-history',
        rows: all,
        columns: [
          { header: 'Partner name', accessor: (r) => getDisplayName(r.partnerName) ?? '' },
          { header: 'Type', accessor: (r) => r.type },
          { header: 'Contract location', accessor: (r) => r.contractLocation || '—' },
          { header: 'Contract status', accessor: (r) => r.contractStatus || '—' },
          { header: 'Global MPN ID', accessor: (r) => r.globalMpnId || '—' },
          { header: 'Location MPN ID', accessor: (r) => r.mpnId || '—' },
          { header: 'TPID', accessor: (r) => r.tpid || '—' },
          { header: 'Previous tier', accessor: (r) => r.previousTier },
          { header: 'TTM revenue', accessor: (r) => formatCurrency(r.ttmRevenue) },
          { header: 'Next TTM (for Ops)', accessor: (r) => formatCurrency(r.nextTtm) },
          { header: 'Future TTM', accessor: (r) => formatCurrency(r.futureTtm) },
          { header: 'New tier', accessor: (r) => r.newTier },
          { header: 'Status', accessor: (r) => normalizeStatus(r.status) },
          { header: 'Changed by', accessor: (r) => r.changedBy || '—' },
          {
            header: `Date (${PACIFIC_ZONE})`,
            accessor: (r) => {
              const { date, time } = formatPacificDateTimeParts(r.timestamp);
              return `${date} ${time}`.trim();
            },
          },
        ],
      });
    } catch (err) {
      logApiError('ChangeHistoryPage.export', err);
      setExportError(getFriendlyApiErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  }, [instance, accounts, requestBody, customIncomplete, getDisplayName]);

  const renderTier = (tier: Tier | RetiredTier) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <DiamondFilled style={{ color: getTierColor(tier), fontSize: '16px' }} />
      {tier}
    </span>
  );

  const defaultSortState = useMemo<DataGridProps['defaultSortState']>(
    () => ({ sortColumn: 'dateTime', sortDirection: 'descending' }),
    [],
  );

  const renderCellContent = useCallback(
    (columnId: string, record: TierChangeRecord) => {
      switch (columnId) {
        case 'type':
          return (
            <div className={styles.type}>
              <div className={record.type === 'Promotion' ? styles.typePromotion : styles.typeDemotion}>{record.type === 'Promotion' ? <ArrowUpRegular /> : <ArrowDownRegular />}</div>
              {record.type}
            </div>
          );
        case 'partnerName':
          return <span className={styles.partnerLink}>{getDisplayName(record.partnerName)}</span>;
        case 'contractLocation':
          return record.contractLocation || '—';
        case 'contractStatus':
          return record.contractStatus || '—';
        case 'globalMpnId':
          return record.globalMpnId || '—';
        case 'locationMpnId':
          return record.mpnId || '—';
        case 'tpid':
          return record.tpid || '—';
        case 'previousTier':
          return renderTier(record.previousTier);
        case 'ttmRevenue':
          return formatCurrency(record.ttmRevenue);
        case 'nextTtmForOps':
          return formatCurrency(record.nextTtm);
        case 'futureTtm':
          return formatCurrency(record.futureTtm);
        case 'newTier':
          return renderTier(record.newTier);
        case 'status': {
          const status: ApprovalStatus = normalizeStatus(record.status);
          const iconClass = status === 'Approved' ? styles.statusApproved
          : status === 'Denied' ? styles.statusDenied
          : styles.dash;
          const statusIcon =
            status === 'Approved' ? (
              <CheckmarkCircleFilled className={iconClass} />
            ) : status === 'Denied' ? (
              <DismissCircleFilled className={iconClass} />
            ) : (
              <ArrowClockwiseDashesRegular className={iconClass} />
            );
          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {statusIcon}
              {status}
            </span>
          );
        }
        case 'changedBy':
          return record.changedBy || '—';
        case 'dateTime': {
          const { date, time } = formatPacificDateTimeParts(record.timestamp);
          return (
            <div>
              <div>{date},</div>
              <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{time}</div>
            </div>
          );
        }
        default:
          return null;
      }
    },
    [styles, getDisplayName],
  );

  const dataGridElement = useMemo(
    () => (
      <DataGrid
        items={records}
        columns={columns}
        sortable
        resizableColumns
        resizableColumnsOptions={{ autoFitColumns: false }}
        columnSizingOptions={columnSizingOptions}
        defaultSortState={defaultSortState}
        size="medium"
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<TierChangeRecord>>
          {({ item, rowId }) => (
            <DataGridRow<TierChangeRecord> key={rowId}>
              {({ columnId }) => (
                <DataGridCell>
                  <TableCellLayout>{renderCellContent(columnId as string, item)}</TableCellLayout>
                </DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    ),
    [records, columnSizingOptions, defaultSortState, renderCellContent],
  );

    const summaryCards = useMemo(() => [
      { label: 'Total changes', value: cardTotal,   accentColor: SUMMARY_CARD_COLORS.brand,    filterValue: 'All' },
      { label: 'Promotions',    value: promotions,  accentColor: SUMMARY_CARD_COLORS.approved, filterValue: 'Promotion' },
      { label: 'Demotions',     value: demotions,   accentColor: SUMMARY_CARD_COLORS.denied,   filterValue: 'Demotion' },
    ], [cardTotal, promotions, demotions]);

  return (
    <div className={styles.pageRoot}>
      <div className={styles.header}>
        <Title2>Change history</Title2>
        <div className={styles.subtitleRow}>
          <Body1>
            View all historical promotions and demotions, including who made the change and when.
          </Body1>
        </div>
      </div>

      {error && (
        <div className={styles.messageBarWrapper}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Error</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {exportError && (
        <div className={styles.messageBarWrapper}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Export failed</MessageBarTitle>
              {exportError}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <SummaryCardBar
        cards={summaryCards}
        isLoading={isLoading && !summary}
        activeFilter={filterType}
        onCardClick={(value) => setFilterType(value as 'All' | ChangeType)}
      />
      
      <div className={styles.exportRow}>
        <Button
          appearance="subtle"
          icon={isExporting ? <Spinner size="extra-tiny" /> : <ArrowDownloadRegular />}
          onClick={handleExport}
          disabled={!canExport}
        >
          Export to CSV
        </Button>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Search</label>
          <SearchBox
            placeholder="Partner name, MPN ID, TPID"
            value={searchInput}
            onChange={(_e, data) => setSearchInput(data.value)}
            style={{ minWidth: '260px' }}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterType">Type</label>
          <Select
            id="filterType"
            value={filterType}
            onChange={(_e, data) => setFilterType(data.value as 'All' | ChangeType)}
          >
            <option>All</option>
            <option>Promotion</option>
            <option>Demotion</option>
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterStatus">Status</label>
          <Select
            id="filterStatus"
            value={filterStatus}
            onChange={(_e, data) => {
              const v = data.value;
              setFilterStatus(v === 'All' || v === 'Approved' || v === 'Denied' ? v : 'All');
            }}
          >
            <option value="All">All</option>
            <option value="Approved">Approved</option>
            <option value="Denied">Denied</option>
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterPreviousTier">Previous tier</label>
          <Select id="filterPreviousTier" value={filterPreviousTier} onChange={(_e, data) => setFilterPreviousTier(data.value)}>
            <option>All</option>
            {TIER_OPTIONS.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterNewTier">New tier</label>
          <Select id="filterNewTier" value={filterNewTier} onChange={(_e, data) => setFilterNewTier(data.value)}>
            <option>All</option>
            {TIER_OPTIONS.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterTimeRange">Time range</label>
          <Select
            id="filterTimeRange"
            value={filterTimeRange}
            onChange={(_e, data) => {
              const next = data.value as TimeRangeLabel;
              setFilterTimeRange(next);
              if (next === 'Custom') {
                const times = records
                  .map((r) => new Date(r.timestamp).getTime())
                  .filter((t) => Number.isFinite(t));
                if (times.length > 0) {
                  const toIsoDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);
                  if (!dateFrom) setDateFrom(toIsoDate(Math.min(...times)));
                  if (!dateTo) setDateTo(toIsoDate(Math.max(...times)));
                }
                requestAnimationFrame(() => {
                  const input = dateFromInputRef.current;
                  if (!input) return;
                  input.focus();
                  const maybePicker = input as HTMLInputElement & { showPicker?: () => void };
                  try {
                    maybePicker.showPicker?.();
                  } catch {
                    // showPicker can throw if the input is not visible yet; focus is enough fallback.
                  }
                });
              }
            }}
          >
            {TIME_RANGES.map((label) => (
              <option key={label}>{label}</option>
            ))}
          </Select>
        </div>
        {filterTimeRange === 'Custom' && (
          <>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel} htmlFor="filterDateFrom">Date From</label>
              <Input
                id="filterDateFrom"
                type="date"
                value={dateFrom}
                onChange={(_e, data) => setDateFrom(data.value)}
                input={{ ref: dateFromInputRef }}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel} htmlFor="filterDateTo">Date To</label>
              <Input
                id="filterDateTo"
                type="date"
                value={dateTo}
                onChange={(_e, data) => setDateTo(data.value)}
              />
            </div>
          </>
        )}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterChangedBy">Changed by</label>
          <Input
            id="filterChangedBy"
            placeholder="Name contains…"
            value={filterChangedBy}
            onChange={(_e, data) => setFilterChangedBy(data.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} aria-hidden="true">&nbsp;</label>
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={clearAllFilters}
            disabled={!hasActiveFilters}
            title="Clear all filters"
          >
            Clear filters
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Text size={200}>
            Showing {totalChanges === 0 ? 0 : startIndex + 1} - {endIndex} of {totalChanges}
          </Text>
          {isLoading && records.length > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Spinner size="extra-tiny" />
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Updating…</Text>
            </span>
          )}
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip content="First page" relationship="label">
              <Button
                appearance="subtle"
                icon={<ChevronDoubleLeftRegular />}
                disabled={currentPage <= 1}
                onClick={() => goToPage(1)}
                size="small"
              />
            </Tooltip>
            <Tooltip content="Previous page" relationship="label">
              <Button
                appearance="subtle"
                icon={<ChevronLeftRegular />}
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
                size="small"
              />
            </Tooltip>
            <Text size={200} style={{ whiteSpace: 'nowrap' }}>Page</Text>
            <Select
              value={String(currentPage)}
              onChange={(_e, data) => goToPage(Number(data.value))}
              size="small"
              aria-label="Go to page"
              style={{ minWidth: 'auto', width: 'auto' }}
            >
              {pageOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Select>
            <Text size={200} style={{ whiteSpace: 'nowrap' }}>of {totalPages}</Text>
            <Tooltip content="Next page" relationship="label">
              <Button
                appearance="subtle"
                icon={<ChevronRightRegular />}
                disabled={currentPage >= totalPages}
                onClick={() => goToPage(currentPage + 1)}
                size="small"
              />
            </Tooltip>
            <Tooltip content="Last page" relationship="label">
              <Button
                appearance="subtle"
                icon={<ChevronDoubleRightRegular />}
                disabled={currentPage >= totalPages}
                onClick={() => goToPage(totalPages)}
                size="small"
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div ref={tableWrapperRef} className={styles.tableWrapper}>
        {isLoading && records.length === 0 ? (
          <Skeleton>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              {Array.from({ length: 8 }, (_, i) => (
                <SkeletonItem key={`h-${i}`} style={{ width: `${80 + (i % 3) * 20}px`, height: '14px' }} />
              ))}
            </div>
            {Array.from({ length: 8 }, (_, row) => (
              <div key={`r-${row}`} style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                {Array.from({ length: 8 }, (_, col) => (
                  <SkeletonItem key={`c-${col}`} style={{ width: `${80 + (col % 3) * 20}px`, height: '14px' }} />
                ))}
              </div>
            ))}
          </Skeleton>
        ) : customIncomplete ? (
          <div className={styles.emptyState}>
            <Text size={300}>Select both Date From and Date To to view results.</Text>
          </div>
        ) : records.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={300}>No tier changes found matching your current filters.</Text>
          </div>
        ) : (
          <div
            style={{
              opacity: isLoading ? 0.55 : 1,
              pointerEvents: isLoading ? 'none' : 'auto',
              transition: 'opacity 120ms ease-out',
            }}
          >
            {dataGridElement}
          </div>
        )}
      </div>
    </div>
  );
};
